"use client";

import { Box } from "@revoo/design-system/ui/Box/Box";
import { Button } from "@revoo/design-system/ui/Button/Button";
import { Field } from "@revoo/design-system/ui/Form/Field/Field";
import { Flex } from "@revoo/design-system/ui/Flex/Flex";
import { Header3 } from "@revoo/design-system/ui/Typography/Header3";
import { Input } from "@revoo/design-system/ui/Form/Input/Input";
import { Text1 } from "@revoo/design-system/ui/Typography/Text1";
import { Checkbox } from "@revoo/design-system/ui/Form/Checkbox/Checkbox";
import { Icon } from "@revoo/design-system/ui/Icon/Icon";
import { Trans, useTranslation } from "react-i18next";
import React, { type FormEvent, useEffect } from "react";
import { useRouter } from "next/navigation";
import { InputPsw } from "@revoo/design-system/ui/Form/InputPsw/InputPsw";
import { useFormContext } from "react-hook-form";
import useRegister from "@/libs/auth/pages/Signup/useRegister";
import { useSignupContext } from "@/libs/auth/providers/SignupProvider";
import { SignupStartModel } from "@/libs/auth/redux/signup.types";
import { GDPR_LINK, TERMS_LINK } from "@/libs/shared/constants";

const SignupStart = () => {
  const { signupForm, setSignupForm } = useSignupContext();
  const { t } = useTranslation();

  const router = useRouter();

  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = useFormContext();

  const { register, isLoading, isSuccess } = useRegister();

  useEffect(() => {
    setSignupForm(signupForm);
    isSuccess && router.push("/signup/1");
  }, [isSuccess]);

  const handleRegister = (signupStart: SignupStartModel) => {
    setSignupForm(signupStart);
    void register(signupStart);
  };

  return (
    <Box
      as="form"
      onSubmit={(event: FormEvent<HTMLFormElement>) =>
        void handleSubmit(handleRegister as any)(event)
      }
    >
      <Flex
        w={["auto", "auto", "auto", "lg"]}
        margin={[12, "0 auto"]}
        bg="palette.secondary.100"
        py={4}
        px={6}
        borderRadius="lg"
        position="relative"
        zIndex={100}
        direction="column"
      >
        <Header3 mb={6} fontWeight="700">
          <Trans i18nKey="Signup:title" />
        </Header3>
        <Flex
          direction="column"
          gap={4}
          maxH={{
            base: "auto",
            md: "50vh",
          }}
          overflow="auto"
          px={2}
        >
          <Field
            component={Input as any}
            control={control as any}
            autoComplete="off"
            name="email"
            size="sm"
            label={
              <Text1 size="sm">
                <Trans i18nKey="Signup:form:email" />*
              </Text1>
            }
          />
          <Field
            component={InputPsw as any}
            control={control as any}
            autoComplete="new-password"
            size="sm"
            name="password"
            label={
              <Text1 size="sm">
                <Trans i18nKey="Signup:form:password" />*
              </Text1>
            }
          />
          {/* <Field
            component={Input as any}
            name="referral_code"
            size="sm"
            label={
              <Text1 size="sm">
                <Trans i18nKey="Signup:form:referral_code" />
              </Text1>
            }
            control={control as any}
          />*/}
          <Flex direction="column" gap={4} mt={2}>
            <Field
              component={Checkbox}
              name="gdpr_terms"
              checkboxLabel={
                <Flex
                  display={{
                    base: "ruby",
                    md: "flex",
                  }}
                >
                  <Text1 fontSize="sm">
                    <Text1 as="u">
                      <a
                        target="_blank"
                        href={GDPR_LINK}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Trans i18nKey="Signup:form:gdpr" />
                      </a>
                      {`, `}
                      <a
                        target="_blank"
                        href={TERMS_LINK}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Trans i18nKey="Signup:form:terms" />
                      </a>
                    </Text1>
                    {` *`}
                  </Text1>
                </Flex>
              }
              control={control as any}
            />
            <Field
              width="sm"
              name="marketing"
              component={Checkbox}
              control={control as any}
              checkboxLabel={
                <Text1 as="span" fontSize="sm">
                  <Trans i18nKey="Signup:form:marketing" />
                </Text1>
              }
            />
          </Flex>
        </Flex>
        <Flex
          justifyContent="center"
          mt={{
            base: 8,
            md: 4,
          }}
        >
          <Button
            variant="solid"
            size="md"
            minW={40}
            type="submit"
            isDisabled={!isValid}
            isLoading={isLoading}
          >
            <Trans i18nKey="Signup:register" />
          </Button>
        </Flex>
        <Flex
          minH={4}
          mt={{
            base: 8,
            md: 4,
          }}
          alignItems="center"
          justifyContent="center"
        >
          <Flex
            w={72}
            minH={10}
            bg="palette.info.80"
            borderRadius={4}
            justifyContent="center"
            alignItems="center"
          >
            <Icon boxSize={6} icon="IcDraft" />
            <Box
              fontSize="xs"
              textAlign="center"
              fontWeight={600}
              dangerouslySetInnerHTML={{
                __html: t("Signup:no_payment_method") || "",
              }}
            />
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};

export default SignupStart;
